import React, { ChangeEvent, Fragment } from "react";
import { RouteComponentPropsHistory } from "../../types";

import withTitle from "../withTitle";
import { V2 } from "../../Api";
import { formatDateLocal, formatName, formatDateLocalMonth } from "../../Utilities";
import DelayInput from "../DelayInput";
import { DateTime } from "luxon";

interface IBonusSelfViewState {
    isLoading: string | boolean;
    isInteractive: boolean;

    searchMonth: string;

    summary: any;
}

class BonusSelfView extends React.Component<RouteComponentPropsHistory<any, any>, IBonusSelfViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any>>) {
        super(props);

        const state: IBonusSelfViewState = {
            isLoading: true,
            isInteractive: !props.match.params.id,

            searchMonth: (props.location.state && props.location.state.searchMonth) || DateTime.local().toFormat("yyyy-MM"),

            summary: { items: [] },
        };

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
    }

    public componentDidMount() {
        this.loadData();
    }

    public render() {
        const summaryTable = <table className="table-vertical">
            <thead>
                <tr>
                    <th>Item</th>
                    <th className="col-smallest">Cases</th>
                    <th className="col-smallest">Rate</th>
                    <th className="col-smallest">Unit</th>
                    <th className="col-smallest">Amount</th>
                    <th className="col-smallest">Total</th>
                </tr>
            </thead>
            <thead className="table-vertical-head-small">
                <tr>
                    <th>Summary</th>
                </tr>
            </thead>
            <tbody>
                {this.state.summary.items.map((a: any) => this.renderSummaryLine(a))}
                {this.state.summary.items.length > 0 && this.state.summary.group !== 2
                    ? <tr>
                        <td colSpan={4} className="col-right-vt"><b>Grand Total</b></td>
                        <td className="col-right-vt" />
                        <td className="col-right-vt">{!this.state.isLoading ? this.formatAmount(this.state.summary.total, 1) : ""}</td>
                    </tr>
                    : null}
            </tbody>
        </table>;

        const caseTable = <table className="table-vertical">
            <thead>
                <tr>
                    <th className="col-smallest">Date</th>
                    <th>Name</th>
                    <th>Operation</th>
                    {this.state.summary.group === 2 ? <th className="col-smallest">wRVU</th> : null}
                    {this.state.summary.group === 2 ? <th className="col-smallest">Type</th> : null}
                </tr>
            </thead>
            <thead className="table-vertical-head-small">
                <tr>
                    <th>Details</th>
                </tr>
            </thead>
            <tbody>
                {this.state.summary.cases && this.state.summary.cases.map((a: any) => <tr key={a.surgeryId}>
                    <td>{formatDateLocal(a.date)}</td>
                    <td>{formatName(a.patientName)}</td>
                    <td>{a.operationName}</td>
                    {this.state.summary.group === 2 ? <td className="col-right-vt inline" data-caption="wRVU: ">{a.value}</td> : null}
                    {this.state.summary.group === 2 ? <td className="col-right-vt">{a.group === 0 ? "Primary" : "Assist"}</td> : null}
                </tr>)}
            </tbody>
        </table>;

        return (
            <div className="viewer-root">
                {this.state.isInteractive
                    ? <div className="filter-control-box">
                        <DelayInput type="month" timeout={300} name="searchMonth" value={this.state.searchMonth} onChangeDelayed={this.handleElementChange} />
                    </div>
                    : null}

                {this.state.isLoading === true
                    ? <div className="spinner spinner-center lds-dual-ring-big" />
                    : typeof this.state.isLoading === "string"
                        ? <div className="error-text">{this.state.isLoading}</div>
                        : <Fragment>
                            {!this.state.isInteractive
                                ? <h3>{formatDateLocalMonth(this.state.summary.month)} &mdash; Summary</h3>
                                : <h3>Summary</h3>}
                            {summaryTable}
                            {this.state.summary.isPreview ? <small><br /><em>These values are preliminary and are subject to change.</em></small> : null}

                            <h3>Case details &mdash; {this.state.summary.caseCount}</h3>
                            {caseTable}
                        </Fragment>
                }
            </div>
        );
    }

    private async loadData() {
        try {
            this.setState({
                isLoading: true,
            });

            var data = await V2.get<any>(
                this.state.isInteractive
                    ? `bonus/own/${this.state.searchMonth}`
                    : `bonus/set/${this.props.match.params.id}`
            ) || { items: [], cases: [] };

            data.items?.sort((a: any, b: any) => a.id.localeCompare(b.id));
            data.cases?.sort((a: any, b: any) => a.date.localeCompare(b.date));

            this.setState({
                isLoading: false,

                summary: data
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load data",
            });
        }
    }

    private unitToString(unit: number) {
        switch (unit) {
            case 0: return "Points";
            case 1: return "USD";
            case 2: return "wRVU";
            default: return unit.toString();
        }
    }

    private formatAmount(amount: string | number, unit: number) {
        switch (unit) {
            case 0: return amount;
            case 1: return (+amount).toLocaleString(undefined, { style: "currency", currency: "USD" });
            default: return amount;
        }
    }

    private renderSummaryLine(a: any) {
        if (a.type === 0) {
            return <tr key={a.id}>
                <td>{a.name}</td>
                <td className="col-right-vt">{a.caseCount}</td>
                <td className="col-right-vt">{a.rate !== undefined ? this.formatAmount(a.rate, a.unit) : null}</td>
                <td className="col-center"><small>{this.unitToString(a.unit)}</small></td>
                <td className="col-right-vt">{this.formatAmount(a.value, a.unit)}</td>
                <td className="col-right-vt">{a.unit === 1 ? this.formatAmount(a.value, a.unit) : ""}</td>
            </tr>;
        }
        else if (a.type === 1) {
            return <tr key={a.id}>
                <td colSpan={4} className="col-right-vt"><b>{a.name}</b></td>
                <td className="col-right-vt">{this.formatAmount(a.value, a.unit)}</td>
                <td className="col-right-vt">{this.formatAmount(a.total, 1)}</td>
            </tr>;
        }
        else if (a.type === 2) {
            return <tr key={a.id}>
                <td>{a.name}</td>
                <td className="col-right-vt">{a.caseCount}</td>
                <td colSpan={4} />
            </tr>;
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value,
        } as any, () => { this.saveSearchState(); this.loadData(); });
    }

    private saveSearchState() {
        // Stash the currently displayed month so the user can come back to it
        const currentLocation = this.props.location;
        currentLocation.state = {
            searchMonth: this.state.searchMonth,
        };
        this.props.history.replace(currentLocation);
    }
}

export default withTitle("Your Bonuses", BonusSelfView);
export const BonusDetailsView = withTitle("Bonus Details", BonusSelfView);